import { useState, useEffect } from 'react';
import { Radio, Plus, X, Copy, Loader2, Trash2, Play, Eye, EyeOff, Shield, Activity, ChevronRight, Check, AlertTriangle } from 'lucide-react';
import FeatureGate from '../components/FeatureGate';
import { InfoTip } from '../components/ui';
import {
  listWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook, getWebhookLog,
  getExchangeConnections,
} from '../services/api';

// Only two modes are wired end-to-end:
//   - create_alert: synchronous; drops alert into user's notification inbox
//   - execute_trade: queued; tradingViewExecutorWorker places the real order
// trigger_bot was removed when the legacy bot system was dismantled.
const ACTION_MODES = [
  { id: 'create_alert',  label: 'Just notify me',                  desc: 'Log alert in your notification feed. No trade fires.',                              icon: '🔔' },
  { id: 'execute_trade', label: 'Execute a trade automatically',   desc: 'Place a real market buy/sell on a connected CEX when the alert fires.',             icon: '⚡' },
];

export default function WebhookSettingsPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createdWebhook, setCreatedWebhook] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const [logEntries, setLogEntries] = useState([]);

  useEffect(() => { load(); }, []);

  async function load() {
    try { const d = await listWebhooks(); setWebhooks(d.webhooks || []); } catch {}
    setLoading(false);
  }

  async function handleToggleActive(webhookKey, currentlyActive) {
    try { await updateWebhook(webhookKey, { active: !currentlyActive }); await load(); }
    catch (err) { alert(err.message); }
  }

  async function handleDelete(webhookKey) {
    if (!confirm('Delete this webhook? TradingView alerts to this URL will stop working immediately.')) return;
    try { await deleteWebhook(webhookKey); await load(); }
    catch (err) { alert(err.message); }
  }

  async function handleTest(webhookKey) {
    try {
      const res = await testWebhook(webhookKey);
      // Surface the actual outcome message — it varies by action mode.
      alert(res?.message || 'Test alert sent. Stats and Recent Alerts have been updated.');
      await load();
    } catch (err) { alert(err.message); }
  }

  async function handleViewLog(webhookKey) {
    setLogFor(webhookKey);
    try { const d = await getWebhookLog(webhookKey); setLogEntries(d.alerts || []); }
    catch { setLogEntries([]); }
  }

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;

  return (
    <FeatureGate feature="webhookAlerts">
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center"><Radio className="w-5 h-5 text-white" /></div>
            TradingView Webhooks
          </h1>
          <p className="text-gray-500 text-sm mt-1">Pipe Pine Script alerts from TradingView into haythix for automated execution</p>
        </div>
        <button onClick={() => { setShowCreate(true); setCreatedWebhook(null); }}
          className="px-4 py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm hover:bg-amber-400 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Create Webhook
        </button>
      </div>

      {webhooks.length === 0 && !showCreate && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 text-center">
          <Radio className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No webhooks yet</p>
          <p className="text-gray-600 text-xs mt-1">Create one to start receiving TradingView alerts</p>
        </div>
      )}

      {/* Webhook list */}
      <div className="space-y-3">
        {webhooks.map(wh => {
          const apiBase = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;
          const url = `${apiBase}/webhook/tradingview/${wh.webhookKey}`;
          return (
            <div key={wh.webhookKey} className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-bold">{wh.name}</h3>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${wh.active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-500/15 text-gray-500'}`}>
                      {wh.active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-blue-500/15 text-blue-400">
                      {ACTION_MODES.find(m => m.id === wh.actionMode)?.label}
                    </span>
                  </div>
                  {wh.description && <p className="text-gray-500 text-xs mt-1">{wh.description}</p>}
                </div>
                <button onClick={() => handleToggleActive(wh.webhookKey, wh.active)}
                  className={`relative w-11 h-6 rounded-full ${wh.active ? 'bg-emerald-500' : 'bg-dark-600'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white ${wh.active ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>

              <div className="bg-dark-700/30 rounded-xl p-3 mb-3 border border-dark-600/30">
                <p className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                  Webhook URL (paste in TradingView)
                  <InfoTip side="top">Paste this into TradingView alert → Notifications → Webhook URL field. The JSON message body must contain your shared secret or the fire will be rejected.</InfoTip>
                  :
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[10px] text-amber-400 font-mono break-all">{url}</code>
                  <button onClick={() => { navigator.clipboard.writeText(url); }} className="p-1.5 rounded-lg bg-dark-600 hover:bg-dark-500"><Copy className="w-3.5 h-3.5 text-gray-400" /></button>
                </div>
                <p className="text-amber-400 text-[10px] mt-2 flex items-center gap-1"><Shield className="w-3 h-3" /> Keep this URL private — anyone with it can trigger your alerts</p>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                <div className="bg-dark-700/30 rounded-lg p-2.5 text-center">
                  <p className="text-gray-500 text-[10px] flex items-center justify-center gap-1">
                    Received
                    <InfoTip side="top">Total TradingView fires that hit this webhook URL with a valid shared secret.</InfoTip>
                  </p>
                  <p className="text-white font-bold mt-0.5">{wh.alertsReceived || 0}</p>
                </div>
                <div className="bg-dark-700/30 rounded-lg p-2.5 text-center">
                  <p className="text-gray-500 text-[10px] flex items-center justify-center gap-1">
                    Successful
                    <InfoTip side="top">Notify mode: logged to your feed. Execute mode: order placed on exchange and confirmed filled.</InfoTip>
                  </p>
                  <p className="text-emerald-400 font-bold mt-0.5">{wh.successfulExecutions || 0}</p>
                </div>
                <div className="bg-dark-700/30 rounded-lg p-2.5 text-center">
                  <p className="text-gray-500 text-[10px] flex items-center justify-center gap-1">
                    Failed
                    <InfoTip side="top">Bad secret, unknown symbol, exchange disconnected, or insufficient balance. Open Recent Alerts to see the exact reason.</InfoTip>
                  </p>
                  <p className={`font-bold mt-0.5 ${wh.failedExecutions ? 'text-red-400' : 'text-gray-500'}`}>{wh.failedExecutions || 0}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => handleTest(wh.webhookKey)}
                  className="flex-1 px-3 py-2 rounded-lg text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 flex items-center justify-center gap-1">
                  <Play className="w-3 h-3" /> Send Test Alert
                </button>
                <button onClick={() => handleViewLog(wh.webhookKey)}
                  className="flex-1 px-3 py-2 rounded-lg text-xs bg-dark-700 text-gray-300 hover:bg-dark-600 flex items-center justify-center gap-1">
                  <Activity className="w-3 h-3" /> Recent Alerts
                </button>
                <button onClick={() => handleDelete(wh.webhookKey)}
                  className="px-3 py-2 rounded-lg text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Setup instructions for newly-created webhook */}
      {createdWebhook && <CreatedWebhookInstructions webhook={createdWebhook} onClose={() => setCreatedWebhook(null)} />}

      {/* Create webhook modal */}
      {showCreate && !createdWebhook && (
        <CreateWebhookModal onClose={() => setShowCreate(false)} onCreated={(wh) => { setShowCreate(false); setCreatedWebhook(wh); load(); }} />
      )}

      {/* Log viewer modal */}
      {logFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70" onClick={() => setLogFor(null)} />
          <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-5 border-b border-dark-700/50 flex items-center justify-between">
              <h3 className="text-white font-bold">Recent Alerts</h3>
              <button onClick={() => setLogFor(null)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-2">
              {logEntries.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No alerts received yet</p>
              ) : logEntries.map(entry => (
                <div key={entry.alertId} className={`rounded-xl p-3 border ${entry.success ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${entry.success ? 'text-emerald-400' : 'text-red-400'}`}>
                      {entry.success ? <Check className="w-3 h-3 inline mr-1" /> : <AlertTriangle className="w-3 h-3 inline mr-1" />}
                      {entry.action || (entry.error || 'received')}
                    </span>
                    <span className="text-gray-600 text-[10px]">{new Date(entry.receivedAt).toLocaleString()}</span>
                  </div>
                  {entry.alert && <pre className="text-[10px] text-gray-400 font-mono mt-1 overflow-x-auto">{JSON.stringify(entry.alert, null, 2)}</pre>}
                  {entry.error && <p className="text-red-300 text-xs mt-1">{entry.error}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </FeatureGate>
  );
}

// ─── Create Webhook Modal ───────────────────────────────────────────────────

function CreateWebhookModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({
    name: '', description: '',
    actionMode: 'create_alert',
    exchangeId: '',
    defaultAmount: '100', defaultSlippageBps: '50',
  });
  const [creating, setCreating] = useState(false);

  // Pull the user's actually-connected exchanges instead of a hardcoded list,
  // so the dropdown reflects reality (Coinbase + Binance US users see those,
  // not a generic "binance/kraken/bybit/okx/kucoin" menu they can't use).
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(false);
  useEffect(() => {
    if (config.actionMode !== 'execute_trade') return;
    setLoadingExchanges(true);
    getExchangeConnections()
      .then(res => {
        const list = (res?.exchanges || res?.data?.exchanges || []).filter(e => e.connected);
        setConnectedExchanges(list);
        if (list.length && !config.exchangeId) {
          setConfig(c => ({ ...c, exchangeId: list[0].exchangeId }));
        }
      })
      .catch(() => setConnectedExchanges([]))
      .finally(() => setLoadingExchanges(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.actionMode]);

  async function handleCreate() {
    setCreating(true);
    try {
      const d = await createWebhook({
        name: config.name, description: config.description,
        actionMode: config.actionMode,
        exchangeId: config.exchangeId || null,
        defaultAmount: parseFloat(config.defaultAmount),
        defaultSlippageBps: parseInt(config.defaultSlippageBps),
      });
      onCreated(d.webhook);
    } catch (err) { alert(err.message); }
    setCreating(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold">Create TradingView Webhook (Step {step}/2)</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <label className="text-gray-500 text-xs block mb-1">Name</label>
              <input value={config.name} onChange={e => setConfig({ ...config, name: e.target.value })} placeholder="e.g., BTC RSI Strategy"
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none" />
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1">Description (optional)</label>
              <textarea value={config.description} onChange={e => setConfig({ ...config, description: e.target.value })} rows={2}
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none" />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-2 flex items-center gap-1">
                When alert fires
                <InfoTip side="right">Notify mode logs to your feed only. Execute mode places a real market order with no manual confirmation. Always test in notify mode first.</InfoTip>
                :
              </label>
              <div className="space-y-2">
                {ACTION_MODES.map(m => (
                  <button key={m.id} onClick={() => setConfig({ ...config, actionMode: m.id })}
                    className={`w-full text-left p-3 rounded-xl border ${config.actionMode === m.id ? 'bg-amber-400/10 border-amber-400/30' : 'bg-dark-700/30 border-dark-600/30'}`}>
                    <div className="flex items-center gap-2">
                      <span>{m.icon}</span>
                      <span className="text-white text-sm font-medium">{m.label}</span>
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5">{m.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => setStep(2)} disabled={!config.name.trim()}
              className="w-full py-3 rounded-xl text-sm font-bold bg-amber-500 text-dark-900 disabled:opacity-40">Next</button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            {config.actionMode === 'execute_trade' && (
              <>
                <div>
                  <label className="text-gray-500 text-xs block mb-1">Exchange (must be connected)</label>
                  {loadingExchanges ? (
                    <div className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-gray-500 text-sm flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  ) : connectedExchanges.length === 0 ? (
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-2.5 text-amber-400 text-xs">
                      No exchange connected. Add one in <a href="/settings?tab=connections" className="underline">Settings → Connections</a> first.
                    </div>
                  ) : (
                    <select value={config.exchangeId} onChange={e => setConfig({ ...config, exchangeId: e.target.value })}
                      className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm">
                      {connectedExchanges.map(ex => (
                        <option key={ex.exchangeId} value={ex.exchangeId}>{ex.name || ex.exchangeId}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                    Default amount (USD or base)
                    <InfoTip side="right">Used only when the TradingView JSON omits "amount". Coinbase market BUY = USD spend; Binance US and sells = base-asset quantity.</InfoTip>
                  </label>
                  <input type="number" value={config.defaultAmount} onChange={e => setConfig({ ...config, defaultAmount: e.target.value })}
                    className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm" />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Used when the TradingView alert doesn't include an explicit <code>amount</code> field. For market BUY on Coinbase, this is interpreted as USD spend; for SELL or other exchanges, as base-asset quantity.
                  </p>
                </div>
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3">
                  <p className="text-red-300 text-xs leading-relaxed">
                    <strong>⚠ Auto-execute fires REAL orders.</strong> The executor worker drains the queue every 60 seconds and places market orders on your exchange with no manual confirmation. Test with create_alert mode first to verify the JSON payload, then switch to execute_trade.
                  </p>
                </div>
              </>
            )}
            <div className="bg-amber-500/5 rounded-xl p-3 border border-amber-500/15">
              <p className="text-amber-300 text-xs">A unique webhook key + shared secret will be generated. Treat both as private.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="flex-1 py-3 rounded-xl text-sm bg-dark-700 text-gray-300">Back</button>
              <button onClick={handleCreate} disabled={creating}
                className="flex-1 py-3 rounded-xl text-sm font-bold bg-amber-500 text-dark-900 disabled:opacity-40 flex items-center justify-center gap-2">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Webhook
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Setup instructions after creation ──────────────────────────────────────

function CreatedWebhookInstructions({ webhook, onClose }) {
  const apiBase = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;
  const url = `${apiBase}/webhook/tradingview/${webhook.webhookKey}`;
  const exampleJson = JSON.stringify({
    secret: webhook.sharedSecret,
    action: 'buy',
    symbol: 'BTC/USDT',
    price: '{{close}}',
    amount: webhook.defaultAmount || 100,
    comment: 'RSI oversold trigger',
  }, null, 2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold flex items-center gap-2"><Check className="w-5 h-5 text-emerald-400" /> Webhook Created!</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-gray-400 text-sm mb-4">Now set it up in TradingView:</p>
        <ol className="space-y-3 text-sm text-gray-300">
          <li>1. Open TradingView and create or edit an alert.</li>
          <li>2. In the alert dialog, scroll to <strong>"Notifications" → "Webhook URL"</strong>.</li>
          <li>3. Paste this URL:
            <div className="bg-dark-700/50 rounded-lg p-2 mt-1 flex items-center gap-2">
              <code className="flex-1 text-[10px] text-amber-400 font-mono break-all">{url}</code>
              <button onClick={() => navigator.clipboard.writeText(url)} className="text-gray-400 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </li>
          <li>4. In the <strong>"Message"</strong> field, paste this JSON template:
            <div className="bg-dark-700/50 rounded-lg p-3 mt-1 relative">
              <pre className="text-[10px] text-gray-300 font-mono overflow-x-auto">{exampleJson}</pre>
              <button onClick={() => navigator.clipboard.writeText(exampleJson)} className="absolute top-2 right-2 text-gray-400 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </li>
          <li>5. Use TradingView placeholders like <code className="bg-dark-700 px-1 rounded text-[11px]">{`{{close}}`}</code> and <code className="bg-dark-700 px-1 rounded text-[11px]">{`{{ticker}}`}</code> for dynamic values.</li>
          <li>6. Save the alert. The next time it triggers, the webhook fires.</li>
        </ol>
        <button onClick={onClose} className="w-full mt-5 py-3 rounded-xl text-sm font-bold bg-amber-500 text-dark-900">Done</button>
      </div>
    </div>
  );
}
